import { BaseService } from './BaseService';
import { apiService } from './apiService';
import { 
  UserFact, 
  UserFactsResponse, 
  CreateUserFactsRequest, 
  CreateUserFactsResponse,
  UserFactResponse,
  DeleteUserFactResponse,
  UserFactInput,
  UserFactsDashboardResponse
} from '../types/userFacts';

export class UserFactsService extends BaseService {
  constructor() {
    super('UserFactsService', {
      enableCircuitBreaker: true,
      enablePerformanceTracking: true,
      enableRetry: true,
      timeout: 30000,
      maxRetryAttempts: 3,
      retryBaseDelay: 1000,
      failureThreshold: 5,
      debug: false
    });
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retry attempt ${attempt} for ${operationName}`, {
      error: error.message,
      delay,
      attempt
    });
  }
  /**
   * Get user facts with optional search and pagination
   * Backend gets userId from authentication context
   */
  async getUserFacts(
    query?: string, 
    topK: number = 100
  ): Promise<UserFactsResponse> {
    return this.executeOperation(async () => {
      const params = new URLSearchParams();
      if (query && query.trim()) {
        params.append('query', query.trim());
      }
      if (topK !== 25) {
        params.append('top_k', topK.toString());
      }
      
      const endpoint = `/api/users/facts${params.toString() ? `?${params.toString()}` : ''}`;
      
      return await apiService.get<UserFactsResponse>(endpoint);
    }, 'getUserFacts');
  }

  /**
   * Get facts dashboard with numeric history and string facts
   * Backend gets userId from authentication context
   * @param since - ISO date string for start of period (default: 30 days ago)
   * @param limit - Max records to fetch (1-1000, default: 500)
   */
  async getDashboard(
    since?: string,
    limit: number = 500
  ): Promise<UserFactsDashboardResponse> {
    return this.executeOperation(async () => {
      const params = new URLSearchParams();
      if (since) {
        params.append('since', since);
      }
      if (limit !== 500) {
        params.append('limit', limit.toString());
      }
      
      const endpoint = `/api/users/facts/dashboard${params.toString() ? `?${params.toString()}` : ''}`;
      
      return await apiService.get<UserFactsDashboardResponse>(endpoint);
    }, 'getDashboard');
  }

  /**
   * Create or update multiple user facts
   * Backend gets userId from authentication context
   */
  async createOrUpdateFacts(
    facts: UserFactInput[], 
    merge: boolean = true
  ): Promise<CreateUserFactsResponse> {
    return this.executeOperation(async () => {
      const request: CreateUserFactsRequest = {
        facts,
        merge
      };
      
      const endpoint = `/api/users/facts`;
      
      return await apiService.put<CreateUserFactsResponse>(endpoint, request);
    }, 'createOrUpdateFacts');
  }

  /**
   * Get a specific fact by key
   * Returns null if the fact doesn't exist (404)
   * Backend gets userId from authentication context
   */
  async getUserFact(key: string): Promise<UserFactResponse | null> {
    return this.executeOperation(async () => {
      try {
        const endpoint = `/api/users/facts/${encodeURIComponent(key)}`;
        return await apiService.get<UserFactResponse>(endpoint);
      } catch (error: any) {
        // 404 means fact doesn't exist or has expired - this is not an error
        if (error.status === 404 || error.type === 'not_found') {
          return null;
        }
        // Re-throw other errors
        throw error;
      }
    }, 'getUserFact');
  }

  /**
   * Delete a specific fact (soft delete with TTL)
   * Backend gets userId from authentication context
   */
  async deleteFact(
    key: string
  ): Promise<DeleteUserFactResponse> {
    return this.executeOperation(async () => {
      const endpoint = `/api/users/facts/${encodeURIComponent(key)}`;
      
      return await apiService.delete<DeleteUserFactResponse>(endpoint);
    }, 'deleteFact');
  }

  /**
   * Create a single fact
   * Backend gets userId from authentication context
   */
  async createFact(
    fact: UserFactInput, 
    merge: boolean = true
  ): Promise<CreateUserFactsResponse> {
    return this.createOrUpdateFacts([fact], merge);
  }

  /**
   * Update a single fact
   * Backend gets userId from authentication context
   */
  async updateFact(
    key: string, 
    updates: Partial<UserFactInput>
  ): Promise<CreateUserFactsResponse> {
    return this.executeOperation(async () => {
      // First get the existing fact to merge with updates
      const existingResponse = await this.getUserFact(key);
      
      // If fact doesn't exist, create it instead
      if (!existingResponse) {
        if (!updates.value) {
          throw new Error('Value is required when creating a new fact');
        }
        return await this.createOrUpdateFacts([{
          key,
          value: updates.value,
          source: updates.source,
          confidence: updates.confidence,
          tags: updates.tags,
          ttlSeconds: updates.ttlSeconds
        }], true);
      }
      
      const existing = existingResponse.fact;
      
      const updatedFact: UserFactInput = {
        key: existing.key,
        value: updates.value !== undefined ? updates.value : existing.value,
        source: updates.source !== undefined ? updates.source : existing.source,
        confidence: updates.confidence !== undefined ? updates.confidence : existing.confidence,
        tags: updates.tags !== undefined ? updates.tags : existing.tags,
        ttlSeconds: updates.ttlSeconds !== undefined ? updates.ttlSeconds : existing.ttl
      };
      
      return await this.createOrUpdateFacts([updatedFact], true);
    }, 'updateFact');
  }

  /**
   * Check if a fact is expired (TTL less than 1 minute from updated/created time)
   */
  private isFactExpired(fact: UserFact): boolean {
    if (!fact.ttl) {
      return false; // No TTL means it doesn't expire
    }

    const now = Date.now();
    const updatedTime = new Date(fact.updatedAt).getTime();
    const createdTime = new Date(fact.createdAt).getTime();
    
    // Use the more recent of updated or created time
    const lastModified = Math.max(updatedTime, createdTime);
    
    // Calculate time elapsed since last modification in seconds
    const elapsedSeconds = (now - lastModified) / 1000;
    
    // Check if the fact has expired (TTL - elapsed time < 60 seconds)
    const remainingTtl = fact.ttl - elapsedSeconds;
    
    return remainingTtl < 60; // Expired if less than 1 minute remaining
  }

  /**
   * Search facts locally (client-side filtering)
   */
  searchFactsLocally(facts: UserFact[], query: string): UserFact[] {
    // First filter out expired facts
    const activeFacts = facts.filter(fact => !this.isFactExpired(fact));
    
    if (!query || !query.trim()) {
      return activeFacts;
    }

    const searchTerms = query.toLowerCase().trim().split(/\s+/);
    
    return activeFacts.filter(fact => {
      const searchableText = [
        fact.key,
        String(fact.value),
        fact.source,
        ...fact.tags
      ].join(' ').toLowerCase();
      
      // OR logic: fact matches if it contains any of the search terms
      return searchTerms.some(term => searchableText.includes(term));
    });
  }

  /**
   * Normalize a fact key (similar to backend normalization)
   */
  normalizeKey(key: string): string {
    return key.trim().toLowerCase().replace(/\s+/g, '_');
  }

  /**
   * Validate fact input
   */
  validateFactInput(fact: UserFactInput): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!fact.key || !fact.key.trim()) {
      errors.push('Key is required and cannot be empty');
    }
    
    if (fact.value === undefined || fact.value === null || fact.value === '') {
      errors.push('Value is required and cannot be empty');
    }
    
    if (fact.confidence !== undefined && (fact.confidence < 0 || fact.confidence > 1)) {
      errors.push('Confidence must be between 0 and 1');
    }
    
    if (fact.ttlSeconds !== undefined && fact.ttlSeconds <= 0) {
      errors.push('TTL seconds must be positive');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const userFactsService = new UserFactsService();
